import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useMarkProBuilderSeen } from '~/hooks/mutations/use-pro-builder';
import { useFeatureFlags } from '~/hooks/queries/use-feature-flags';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { useCodingOpenStore } from '~/lib/stores/coding-open-store';
import { shouldCloseForNewOperatorPanelRequest } from '~/lib/stores/operator-panel-close-gate';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { useCloseOnOperatorPanelRequest } from '~/lib/stores/use-close-on-operator-panel-request';
import { ProBuilderBody } from './pro-builder-body';
import { ProBuilderWindow } from './pro-builder-window';
import {
  proBuilderFlagFromData,
  shouldHideOnNavigation,
  shouldMarkSeen,
  shouldMountProBuilderWindow,
} from './pro-builder-window-rules';

/**
 * Hides the window the instant a NEW one-shot open request lands in a store
 * (modal viewers are mutually exclusive, never stacked — CLAUDE.md). A request
 * already set when the host mounts is not "new" (same seeding rule as
 * `useCloseOnOperatorPanelRequest`).
 */
function useCloseOnNewRequest(request: unknown, close: () => void): void {
  const previous = useRef(request);
  useEffect(() => {
    if (shouldCloseForNewOperatorPanelRequest(previous.current, request)) close();
    previous.current = request;
  }, [request, close]);
}

/** Hides the window on a location change while it is open (see `shouldHideOnNavigation`). */
function useCloseOnNavigation(close: () => void): void {
  const { pathname } = useLocation();
  const previous = useRef(pathname);
  useEffect(() => {
    const open = useProBuilderOpenStore.getState().open;
    if (shouldHideOnNavigation(previous.current, pathname, open)) close();
    previous.current = pathname;
  }, [pathname, close]);
}

/**
 * FRONT-3040: marks the session's state seen on each visibility edge (see
 * `shouldMarkSeen`), so the board row turns grey once the person has looked.
 * The mutation is only ever called, never reset (it may be pending).
 */
function useMarkSeenOnVisibilityEdge(open: boolean): void {
  const { mutate } = useMarkProBuilderSeen();
  const previous = useRef(open);
  useEffect(() => {
    if (shouldMarkSeen(previous.current, open)) mutate();
    previous.current = open;
  }, [open, mutate]);
}

/** Mounted once in the app shell (`layouts/app-layout.tsx`) so page navigation never unmounts the terminal. */
export function ProBuilderWindowHost() {
  // Deliberately NOT `useFeatureFlag`: that falls back to false on a failed
  // refetch, which is right for showing a doorway (the sidebar, and Settings →
  // Pro Builder with its Restart button, which reads the flag that way on
  // purpose) and wrong here, where an unmount detaches the person from a running Claude Code.
  // This decides from the last flags DATA and unmounts only when it positively
  // says the flag is off. Two different rules for one flag, by design.
  const { data } = useFeatureFlags();
  const everOpened = useProBuilderOpenStore((s) => s.everOpened);
  const open = useProBuilderOpenStore((s) => s.open);
  const close = useProBuilderOpenStore((s) => s.closeProBuilder);
  const wasMounted = useRef(false);
  // Modal viewers are mutually exclusive, never stacked (CLAUDE.md): hide this
  // one the instant the Builder, a chat window, a document or the desktop
  // operator panel is raised, and on any navigation away. The other direction
  // lives in each of those surfaces: they close on this window's open edge
  // (`use-close-on-pro-builder-open.ts`).
  useCloseOnOperatorPanelRequest(close);
  useCloseOnNewRequest(useCodingOpenStore((s) => s.openRequest), close);
  useCloseOnNewRequest(useChatOpenStore((s) => s.openRequest), close);
  useCloseOnNewRequest(useChatOpenStore((s) => s.openDocumentRequest), close);
  useCloseOnNavigation(close);
  useMarkSeenOnVisibilityEdge(open);

  const mounted = shouldMountProBuilderWindow(proBuilderFlagFromData(data), everOpened, wasMounted.current);
  wasMounted.current = mounted;
  if (!mounted) return null;
  return (
    <ProBuilderWindow open={open} onClose={close}>
      <ProBuilderBody onClose={close} />
    </ProBuilderWindow>
  );
}
